<div class="main-content mb-4 mx-lg-4 mx-md-4 mx-sm-0 px-sm-4 px-3">
    <!-- Page Header -->
    <div class="page-title-wrapper">
        <!-- No search input on this page, so the title takes the left slot of the toolbar row
             instead of stacking above it and leaving that space empty. -->
        <div class="d-flex justify-content-between align-items-center flex-row flex-wrap gap-3">
            <ms-page-title />

            <div class="d-flex gap-3">
                <ms-sort-dropdown [sortOptions]="sortOptions" [(selectedSortOption)]="orderBy"
                    (selectedSortOptionChange)="setSortOrder($event)" />

                <!-- One entry point for every role: the eligible-User list is the same whichever is
                     being granted, so the role is chosen after the person, inside the picker. -->
                @if (assignableRoles().length > 0) {
                    <button type="button" class="btn btn-primary" [disabled]="isLoading" (click)="openUserPicker()">
                        <fa-duotone-icon icon="user-plus" secondaryOpacity="0.4" class="me-2" />Assign Role
                    </button>
                }
            </div>
        </div>
    </div>

    @if (errorMessage()) {
        <div class="alert alert-danger" role="alert">
            <fa-duotone-icon icon="circle-exclamation" secondaryOpacity="0.4" class="me-2" />
            {{ errorMessage() }}
        </div>
    } @else {
        <div>
            <div class="mb-0 text-start" #jumpToElementMobile>
                Showing: {{ staffList().length || 0 }} of {{ totalCount | number }}
            </div>

            <!-- Table Start -->
            <div class="table-responsive d-none d-sm-block">
                <table class="table table-hover fixed-header placeholder-glow" msResponsiveTable>
                    <thead #jumpToElement>
                        <tr>
                            <th scope="col" class="sortable" msResponsiveTableColumn="name" [priority]="1"
                                (click)="toggleSortDirection('Name')">
                                Name
                                <fa-duotone-icon icon="sort" class="ms-1" size="lg"
                                    [primaryOpacity]="orderBy == staffListSortOptions.NameASC ? '0.9' : '0.3'"
                                    [secondaryOpacity]="orderBy == staffListSortOptions.NameDESC ? '0.9' : '0.3'"
                                />
                            </th>
                            <th scope="col" class="sortable" msResponsiveTableColumn="role" [priority]="1"
                                (click)="toggleSortDirection('Role')">
                                Role
                                <fa-duotone-icon icon="sort" class="ms-1" size="lg"
                                    [primaryOpacity]="orderBy == staffListSortOptions.RoleASC ? '0.9' : '0.3'"
                                    [secondaryOpacity]="orderBy == staffListSortOptions.RoleDESC ? '0.9' : '0.3'"
                                />
                            </th>
                            <th scope="col" class="sortable" msResponsiveTableColumn="status" [priority]="2"
                                (click)="toggleSortDirection('Status')">
                                Status
                                <fa-duotone-icon icon="sort" class="ms-1" size="lg"
                                    [primaryOpacity]="orderBy == staffListSortOptions.StatusASC ? '0.9' : '0.3'"
                                    [secondaryOpacity]="orderBy == staffListSortOptions.StatusDESC ? '0.9' : '0.3'"
                                />
                            </th>
                            <th scope="col" class="sortable" msResponsiveTableColumn="lastLogin" [priority]="3"
                                (click)="toggleSortDirection('Last Login')">
                                Last Login
                                <fa-duotone-icon icon="sort" class="ms-1" size="lg"
                                    [primaryOpacity]="orderBy == staffListSortOptions.LastLoginASC ? '0.9' : '0.3'"
                                    [secondaryOpacity]="orderBy == staffListSortOptions.LastLoginDESC ? '0.9' : '0.3'"
                                />
                            </th>
                            <th class="fixed-header" scope="col" msResponsiveTableColumn="actions" [priority]="1">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        @for (staffUser of displayStaff; track staffUser.userId) {
                            <tr class="align-middle">
                                <td msResponsiveTableColumn="name" [priority]="1">
                                    <div class="d-flex align-items-center">
                                        <ms-avatar [identity]="staffUser" [avatarSize]="avatarSize" [avatarShape]="avatarShape"
                                            [avatarAdditionalAttributes]="avatarAdditionalAttributes" [isLoading]="isLoading" />

                                        <div class="min-w-0 ms-3">
                                            <div class="fw-bold text-truncate" [class.placeholder]="isLoading"
                                                [msTooltip]="(staffUser.firstName + ' ' + staffUser.lastName).trim()" msTooltipOnlyIfTruncated>
                                                {{ staffUser.firstName + ' ' + staffUser.lastName }}
                                            </div>
                                            <small class="d-none d-sm-block text-truncate" [class.placeholder]="isLoading"
                                                [msTooltip]="staffUser.emailAddress" msTooltipOnlyIfTruncated>{{ staffUser.emailAddress }}</small>
                                        </div>
                                    </div>
                                </td>
                                <td msResponsiveTableColumn="role" [priority]="1">
                                    <span [class.placeholder]="isLoading">{{ staffUser.mostPermissiveRole | noValue }}</span>
                                </td>
                                <td msResponsiveTableColumn="status" [priority]="2">
                                    <ms-status-badge [status]="staffUser.status" [(isLoading)]="isLoading" />
                                </td>
                                <td msResponsiveTableColumn="lastLogin" [priority]="3">
                                    <span [class.placeholder]="isLoading">
                                        {{ staffUser.lastLogin ? ((staffUser.lastLogin + "Z") | date:'MMM dd, yyyy h:mm a') : 'Never' }}
                                    </span>
                                </td>
                                <td msResponsiveTableColumn="actions" [priority]="1">
                                    @if (!isLoading) {
                                        @if (canRemoveAnyRole(staffUser)) {
                                            <button type="button" class="btn btn-sm btn-danger text-nowrap" (click)="openRemoveRoles(staffUser)">
                                                <fa-duotone-icon icon="user-minus" secondaryOpacity="0.4" class="me-2" />Remove Roles
                                            </button>
                                        } @else {
                                            @if (removeRolesBlockedReason(staffUser); as reason) {
                                                <!-- A disabled button emits no pointer events, so the wrapper carries
                                                     the tooltip and the tab stop that explain why it is disabled. -->
                                                <span class="d-inline-block" tabindex="0" [msTooltip]="reason">
                                                    <button type="button" class="btn btn-sm btn-danger text-nowrap" disabled>
                                                        <fa-duotone-icon icon="user-minus" secondaryOpacity="0.4" class="me-2" />Remove Roles
                                                    </button>
                                                </span>
                                            }
                                        }
                                    }
                                </td>
                            </tr>
                        }
                    </tbody>
                    <tfoot ms-pagination [take]="take" (loadMoreInView)="loadMoreInView()" [(skip)]="skip"
                        [hasNext]="hasNext" [itemCount]="displayStaff.length" (jumpToClicked)="jumpToTop($event)">
                    </tfoot>
                </table>
            </div>
            <!-- Table End -->

            <div id="mobileTableCards" class="d-block d-sm-none">
                @for (staffUser of displayStaff; track staffUser.userId) {
                    <div class="card d-flex pt-1 my-3">
                        <div class="d-flex flex-column card-body">
                            <div class="d-flex flex-row">
                                <div class="card-left-column d-block-flex text-center">
                                    <ms-avatar [identity]="staffUser" [avatarSize]="avatarSize" [avatarShape]="avatarShape"
                                        [avatarAdditionalAttributes]="avatarAdditionalAttributes" [isLoading]="isLoading" />
                                </div>

                                <div class="right-card-column w-100">
                                    <h5 class="card-title mb-0" [class.placeholder]="isLoading">{{ staffUser.firstName + ' ' + staffUser.lastName }}</h5>
                                    <div class="fw-semibold" [class.placeholder]="isLoading">{{ staffUser.emailAddress }}</div>
                                    <div class="card-text" [class.placeholder]="isLoading">{{ staffUser.mostPermissiveRole | noValue }}</div>
                                    <!-- Touch has no hover, so the reason the desktop row puts in a tooltip is
                                         written out here instead; the card simply omits its action button. -->
                                    @if (!isLoading && removeRolesBlockedReason(staffUser); as reason) {
                                        <div class="text-secondary small">{{ reason }}</div>
                                    }
                                </div>
                            </div>

                            <div class="d-flex flex-row gap-2 mt-3">
                                <ms-status-badge [status]="staffUser.status" [(isLoading)]="isLoading" />
                            </div>
                        </div>

                        @if (!isLoading && canRemoveAnyRole(staffUser)) {
                            <button class="mobile-action-button dropdown-toggle" data-bs-toggle="dropdown">
                                Actions
                            </button>
                            <ul class="dropdown-menu dropdown-menu-end w-75">
                                <li>
                                    <button type="button" class="dropdown-item" (click)="openRemoveRoles(staffUser)">
                                        <fa-duotone-icon icon="user-minus" secondaryOpacity="0.4" class="me-2" />Remove Roles
                                    </button>
                                </li>
                            </ul>
                        }
                    </div>
                }
                <div ms-in-view-port (inViewPort)="loadMoreInView()" ms-pagination [take]="take" [(skip)]="skip"
                    [hasNext]="hasNext" [itemCount]="displayStaff.length" (jumpToClicked)="jumpToTop($event)">
                </div>
            </div>
        </div>
    }
</div>

<!-- User Picker Modal -->
<div class="modal fade" id="staffUserPickerModal" tabindex="-1" aria-labelledby="staffUserPickerModalLabel" aria-hidden="true">
    <div class="modal-dialog modal-xl modal-dialog-scrollable">
        <div class="modal-content">
            <div class="modal-header">
                <h5 class="modal-title" id="staffUserPickerModalLabel">Assign a Staff Role</h5>
            </div>
            <div class="modal-body">
                <ms-staff-user-picker [selectedUser]="pickerUser()" [roleOptions]="roleOptionsForSelectedUser()"
                    (userChosen)="onPickerUserChosen($event)" (roleSelected)="onRoleSelectedForAssignment($event)" />
            </div>
            <div class="modal-footer">
                @if (pickerUser()) {
                    <button type="button" class="btn btn-ms-secondary me-auto" (click)="backToPickerUsers()">
                        <fa-duotone-icon icon="arrow-left" secondaryOpacity="0.4" class="me-2" />Choose a different User
                    </button>
                }
                <button type="button" class="btn btn-ms-secondary" data-bs-dismiss="modal">Cancel</button>
            </div>
        </div>
    </div>
</div>

<!-- Remove Roles Modal -->
<div class="modal fade" id="removeStaffRolesModal" tabindex="-1" aria-labelledby="removeStaffRolesModalLabel" aria-hidden="true">
    <div class="modal-dialog modal-dialog-centered">
        <div class="modal-content">
            <div class="modal-header">
                <h5 class="modal-title" id="removeStaffRolesModalLabel">Remove Staff Roles</h5>
            </div>
            <div class="modal-body">
                @if (userToManageRoles(); as user) {
                    <div class="d-flex align-items-center mb-4">
                        <ms-avatar [identity]="user" [avatarSize]="avatarSize" [avatarShape]="avatarShape"
                            [avatarAdditionalAttributes]="avatarAdditionalAttributes" />

                        <div class="min-w-0 ms-3">
                            <div class="fw-bold text-truncate">{{ user.firstName + ' ' + user.lastName }}</div>
                            <small class="text-truncate d-block">{{ user.emailAddress }}</small>
                        </div>
                    </div>

                    <div class="d-flex flex-column gap-2">
                        <!-- Every role they hold, including ones this viewer may not remove: seeing the
                             whole picture matters more than hiding the rows without a button. -->
                        @for (userRole of rolesFor(user.userId); track userRole.roleId) {
                            <div class="d-flex justify-content-between align-items-center gap-3 border rounded p-3">
                                <div class="min-w-0">
                                    <div class="fw-semibold">{{ userRole.name }}</div>
                                    @if (!canRemoveRole(user, userRole)) {
                                        <div class="text-secondary small">
                                            {{ StaffManagementMessagingConstants.STAFF_ROLE_REMOVE_NOT_PERMITTED() }}
                                        </div>
                                    }
                                </div>

                                @if (canRemoveRole(user, userRole)) {
                                    <button type="button" class="btn btn-sm btn-danger text-nowrap"
                                        (click)="confirmRemoveRole(userRole)">
                                        <fa-duotone-icon icon="user-minus" secondaryOpacity="0.4" class="me-2" />Remove
                                    </button>
                                }
                            </div>
                        }
                    </div>
                }
            </div>
            <div class="modal-footer">
                <button type="button" class="btn btn-ms-secondary" data-bs-dismiss="modal">Close</button>
            </div>
        </div>
    </div>
</div>

<!-- Assign Role Confirmation Modal -->
<ms-confirmation-modal
    #assignConfirmationModalRef
    modalId="assignStaffRoleConfirmationModal"
    [isSubmitting]="isSubmitting()"
    (confirmed)="onAssignConfirmed()"
    (cancelled)="onAssignCancelled()"
/>

<!-- Unassign Role Confirmation Modal -->
<ms-confirmation-modal
    #unassignConfirmationModalRef
    modalId="unassignStaffRoleConfirmationModal"
    [isSubmitting]="isSubmitting()"
    (confirmed)="onUnassignConfirmed()"
    (cancelled)="onUnassignCancelled()"
/>

<!-- Role Action Result Info Modal -->
<ms-info-modal
    #staffManagementInfoModalRef
    modalId="staffManagementInfoModal"
/>
